import React, { memo, useMemo, useState } from 'react'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  getInputIcon,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { IDispatcher } from 'apps/fleet-planner/types'
import { getEntityById } from 'utils/getEntityById'

interface IDispatcherAutosuggestProps
  extends Omit<IAutosuggestWrapperProps<IDispatcher>, 'fetchList'> {
  dispatchers: IDispatcher[]
}

export const DispatcherAutosuggest = memo<IDispatcherAutosuggestProps>(
  ({
    disabled: disabledProp = false,
    dispatchers,
    error: errorProp,
    large: largeProp = false,
    onAddEntity: onAddEntityProp,
    onBlur: onBlurProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
    value,
  }) => {
    const { isMobile } = useBreakpointContext()

    const [inputValue, setInputValue] = useState('')

    const changeHandler = (option: IAutocompleteOption | undefined) => {
      const dispatcher = getEntityById(dispatchers, option?.id)
      onChangeProp(dispatcher)
    }

    const inputChangeHandler = (val: string = '') => setInputValue(val)

    const parseInputValue = (input: string) => (input.startsWith('Add ') ? '' : input)

    const icon = getInputIcon('search', inputValue)

    const options = useMemo(() => {
      return getAutosuggestOptions({
        options: dispatchers,
        groupIndex: 1,
        groupName: 'Dispatchers',
        entityName: 'New Dispatcher',
        iconName: 'UserIcon',
        inputValue,
        mapOption: ({ fullName: text = '', id = '' }) => ({ text: text ?? '', id: id ?? '' }),
      })
    }, [dispatchers, inputValue])

    return (
      <Autosuggest
        clearable
        dataTest='dispatcher-autosuggest'
        disabled={disabledProp}
        error={errorProp}
        icon={icon}
        id='dispatcher'
        label='Dispatcher'
        large={largeProp}
        onAddEntity={onAddEntityProp}
        onBlur={onBlurProp}
        onCancel={onCancelProp}
        onChange={changeHandler}
        onInputChange={inputChangeHandler}
        options={options}
        parseInputValue={parseInputValue}
        placeholder='Choose a Dispatcher'
        renderOptionType='withAvatar'
        selectOnFocus={!isMobile}
        startAdornmentType='withAvatar'
        value={value}
      />
    )
  }
)
